import React from 'react';
import { sum } from 'lodash';
import { set } from 'lodash/fp';
import { AddOn } from '@upstart/harmony/lib/legos/pencilTypes';
import { fmtMoney } from '@upstart/harmony/lib/formatter';
import Input, { parseFloatValue, PriceInput } from '../Input';
import DataTable from '../../DataTable';
import {
  ADDONS_MODAL_HEADERS,
  ADDONS_MODAL_HEADERS_WITH_DMS_CODE,
} from '../../../util/constants';
import './AddOnsList.scss';

const newAddOn = {
  price: 0,
  cost: 0,
  taxed: false,
  description: '',
  dmsCode: '',
};

interface Props {
  addOns: AddOn[];
  isDMSFlowEnabled: boolean;
  isDMSTypeCDK: boolean;
  setAddOns: (addOns: AddOn[]) => void;
}

function AddOnsList(props: Props) {
  const { addOns, isDMSFlowEnabled, setAddOns, isDMSTypeCDK } = props;

  const totalPrice = sum(addOns.map(addOn => addOn.price));
  const totalCost = sum(addOns.map(addOn => addOn.cost));

  const addRow = () => {
    const newAddOns = [...addOns, newAddOn];
    setAddOns(newAddOns);
  };

  const deleteRow = (i: number) => {
    const newAddOns = addOns.filter(
      (addOn: AddOn, addOnIndex: number) => i !== addOnIndex,
    );
    setAddOns(newAddOns);
  };

  const onPropertyChanged = (i: number, prop: keyof AddOn, value: unknown) => {
    const newAddOns = set(`[${i}].${prop}`, value, addOns);
    setAddOns(newAddOns);
  };

  const getRowInputs = () =>
    addOns.map((addOn, i) => {
      const dmsInput = [];
      dmsInput.push(
        <Input
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onPropertyChanged(i, 'description', e)
          }
          value={addOn.description}
        />,
      );
      if (isDMSFlowEnabled && isDMSTypeCDK) {
        dmsInput.push(
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              onPropertyChanged(i, 'dmsCode', e)
            }
            value={addOn.dmsCode}
          />,
        );
      }
      dmsInput.push(
        ...[
          <PriceInput
            value={addOn.price}
            onChange={(val: string) =>
              onPropertyChanged(i, 'price', parseFloatValue(val))
            }
          />,
          <PriceInput
            value={addOn.cost}
            onChange={(val: string) =>
              onPropertyChanged(i, 'cost', parseFloatValue(val))
            }
          />,
          <Input
            onChange={(val: boolean) =>
              onPropertyChanged(i, 'taxed', !addOn.taxed)
            }
            checked={addOn.taxed}
            type="checkbox"
          />,
          <button
            type="button"
            className="modal-button delete"
            onClick={() => deleteRow(i)}
          >
            Delete
          </button>,
        ],
      );
      return dmsInput;
    });

  const tableHeaders =
    isDMSFlowEnabled && isDMSTypeCDK
      ? ADDONS_MODAL_HEADERS_WITH_DMS_CODE
      : ADDONS_MODAL_HEADERS;
  const rows = getRowInputs();
  return (
    <div className="add-ons-list-container">
      <DataTable
        className="addons-modal-table"
        emptyMessage="No add ons set"
        rows={rows}
        headers={tableHeaders}
      />
      <div className="add-ons-list-footer">
        <button type="button" className="add-ons-add-button" onClick={addRow}>
          <i
            className="glyphicon glyphicon-plus-sign"
            style={{ marginRight: '5px' }}
          />
          Add another Add on
        </button>
        <div>
          <div className="add-ons-total-value">
            Total Price: {fmtMoney(totalPrice, 2)}
          </div>
          <div className="add-ons-total-value">
            Total Cost: {fmtMoney(totalCost, 2)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddOnsList;
